/**
 * Public demo host page.
 *
 * Scans the visitor's own browser and shows, live, which extensions it could identify —
 * the whole thing runs client-side, so nothing ever leaves the page. Also exposes
 * `window.__detectExtensions()`, which the verification harness drives over CDP.
 */

import { detectAll } from '../core/detect';
import { extensions } from '../extensions';
import type { DetectAllOptions, DetectionReport, ExtensionResult, SignalResult } from '../core/types';
import './style.css';

declare global {
  interface Window {
    __detectExtensions: (opts?: DetectAllOptions) => Promise<DetectionReport>;
  }
}

const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;
const esc = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const fmtUsers = (n?: number) =>
  n == null ? '' : `${new Intl.NumberFormat('en', { notation: 'compact' }).format(n)} users`;

// Extensions detectable from *any* page (the ones this demo can actually test). The rest
// are scoped to a specific site and can only be seen from there.
const testable = extensions.filter((e) => !e.requiredOrigin);
const instant = testable.filter((e) => !e.interactive);
const interactive = testable.filter((e) => e.interactive);
const scopedElsewhere = extensions.length - testable.length;

type Hit = { def: (typeof extensions)[number]; kind: SignalResult['kind']; how: string };

const byId = new Map(extensions.map((e) => [e.id, e]));

/** Turn a positive result into the row the UI renders. */
function toHit(result: ExtensionResult): Hit | null {
  if (!result.detected) return null;
  const sig = result.signals.find((s) => s.fired && !s.control);
  return { def: byId.get(result.id)!, kind: sig?.kind ?? 'dom', how: sig?.description ?? '' };
}

/**
 * Scan a batch, reporting hits as they arrive.
 *
 * The core splits the tiers itself — `onResult` fires per extension, and `onFastTier`
 * fires the moment every instant probe is in, which is the point at which the visitor has
 * a usable answer. The behavioural stragglers land afterwards without holding it up.
 */
function scanBatch(
  defs: typeof extensions,
  onHit: (h: Hit) => void,
  onProgress: () => void,
  opts: DetectAllOptions = {},
) {
  return detectAll(defs, {
    ...opts,
    onResult: (result) => {
      const hit = toHit(result);
      if (hit) onHit(hit);
      onProgress();
    },
  });
}

const hits: Hit[] = [];
let deepDone = false;

function hitRow(h: Hit): string {
  const resource = h.kind === 'web-accessible-resource';
  const chip = resource
    ? '<span class="chip chip-resource">web resource</span>'
    : '<span class="chip chip-behavioural">behavioural</span>';
  return `
    <li class="hit">
      <div class="hit-main">
        <div class="hit-name">${esc(h.def.name)}</div>
        <div class="hit-how">${esc(h.how)}</div>
      </div>
      <div class="hit-meta">
        ${h.def.users ? `<span class="hit-users">${fmtUsers(h.def.users)}</span>` : ''}
        ${chip}
      </div>
    </li>`;
}

function shell() {
  $('#scan').innerHTML = `
    <div class="result-head">
      <span class="result-count" id="count">0</span>
      <span class="result-label" id="label">scanning your browser…</span>
    </div>
    <p class="result-sub"><span id="prog"></span> · nothing sent anywhere</p>
    <ul class="hits" id="hitList"></ul>
    <div class="beh-note" id="behNote" hidden><span class="spinner"></span> checking behavioural signals (writing assistants, dark-mode, cursors…)</div>
    <div id="tail"></div>`;
}

function paintHit(h: Hit) {
  hits.push(h);
  $('#count').textContent = String(hits.length);
  $('#hitList').insertAdjacentHTML('beforeend', hitRow(h));
}

function paintProgress(done: number, total: number) {
  $('#prog').textContent = `checked ${done} of ${total} extensions detectable from any page`;
}

function finalize() {
  const none = hits.length === 0;
  $('#count').classList.toggle('none', none && deepDone);
  $('#label').textContent = none
    ? 'detectable extensions found'
    : `of your extensions ${hits.length === 1 ? 'is' : 'are'} detectable`;
  const total = instant.length + (deepDone ? interactive.length : 0);
  $('#prog').textContent = `checked ${total} extensions detectable from any page · ${scopedElsewhere} more only on their own site (Discord, Gmail, …)`;

  const tail = $('#tail');
  if (none && deepDone) {
    tail.innerHTML = `<div class="empty">Nothing this page could identify — either you don't
      have any of the ${total} extensions checked, or the ones you have are among the 172 that
      hide. (Try Grammarly, Adobe Acrobat, or Dark Reader and reload.)</div>`;
  } else if (!deepDone && interactive.length) {
    tail.innerHTML = `<div class="deep">
        <p>The deep scan also checks password managers, which only react to a login form —
           it briefly shows a test form in the corner, then removes it.</p>
        <button id="deep" type="button">Run deep scan</button>
      </div>`;
    $('#deep').addEventListener('click', runDeep);
  } else {
    tail.innerHTML = '';
  }
}

async function runDeep() {
  const btn = $('#deep') as HTMLButtonElement;
  btn.disabled = true;
  btn.textContent = 'Scanning…';
  let done = 0;
  await scanBatch(interactive, paintHit, () => paintProgress(++done + instant.length, instant.length + interactive.length));
  deepDone = true;
  finalize();
}

const RESIST = [
  { n: 103, cat: 'no page surface', title: 'Nothing to touch',
    desc: 'New-tab games, VPN proxies, tab managers, DevTools tools — they never act on an ordinary web page, so a page has nothing to observe.' },
  { n: 43, cat: 'no observable effect', title: 'Leave no trace',
    desc: 'The content script runs, but only reads the page, acts on a keypress, or makes a change with no vendor name on it — nothing a page can pin to a specific extension.' },
  { n: 21, cat: 'hardened resources', title: 'Sealed-off files',
    desc: 'Files exist but no page can reach them — served under a rotating URL token, matched to no origin, or renamed every release. Most of the big ad blockers live here.' },
  { n: 1, cat: 'randomised marker', title: 'A marker made unmatchable',
    desc: 'Bitwarden reacts to login forms but renames its marker every load. (RoboForm looked identical — until its class turned out to be stable, and it moved to the detected column.)' },
  { n: 1, cat: 'outside the page', title: 'A different layer',
    desc: 'A user-agent switcher rewrites an outgoing HTTP header that page JavaScript can never read back.' },
  { n: 3, cat: 'broken manifest', title: 'Promises it doesn’t keep',
    desc: 'Return YouTube Dislike declares web-accessible files it doesn’t actually ship, so every probe fails identically whether or not it is installed.' },
];

function renderResist() {
  $('#resistList').innerHTML = RESIST.map(
    (r) => `
    <li>
      <span class="resist-n">${r.n}</span>
      <span class="resist-body"><strong>${r.title}</strong><span>${r.desc}</span></span>
      <span class="resist-cat">${r.cat}</span>
    </li>`,
  ).join('');
}

async function main() {
  renderResist();
  shell();
  try {
    let done = 0;
    const bump = () => paintProgress(++done, instant.length);
    const note = $('#behNote');
    await scanBatch(instant, paintHit, bump, {
      // The instant tier is complete here — show the count and flag that a handful of
      // behavioural probes are still waiting, rather than leaving the visitor watching a
      // spinner for the ~12s Apollo alone costs.
      onFastTier: () => {
        note.hidden = false;
        finalize();
      },
    });
    note.hidden = true;
    finalize();
  } catch (err) {
    $('#scan').innerHTML = `<div class="empty">Couldn't complete the scan: ${esc(String(err))}</div>`;
  }
}

// The verification harness drives detection over CDP. It passes no cap, so every probe
// gets its full timeout — there, fidelity matters and wall clock does not.
window.__detectExtensions = (opts: DetectAllOptions = {}) => detectAll(extensions, opts);

main();
